import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { useLayering } from '@atlaskit/layering';
import { ButtonMenuItem, FlyoutMenuItemTrigger, MenuList } from '@atlaskit/navigation-system';
import { fg } from '@atlaskit/platform-feature-flags';

import type { NavItemActionsProps } from '../../types';

import messages from './messages';
import { NavItemReorderingMenu } from './nav-item-reordering';
import { NavItemVisibilityToggle } from './nav-item-visibility';

export function NavItemActionsMenu({
	isReorderable = true,
	isVisible = true,
	moveUp,
	moveDown,
	moveToTop,
	moveToBottom,
	toggleVisibility,
	canMoveUp = true,
	canMoveDown = true,
	canToggleVisibility = true,
	onOpenChange,
}: NavItemActionsProps) {
	const { formatMessage } = useIntl();
	// We need to control the state so that nested menus can close this one when they close
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const { currentLevel } = useLayering();

	const handleOpenChange = useCallback(
		(args: OnOpenChangeArgs) => {
			setIsOpen(args.isOpen);
			onOpenChange?.(args);
		},
		[onOpenChange],
	);

	if (!isReorderable && !canToggleVisibility) {
		return null;
	}

	const nestedMenuProps = {
		isReorderable,
		isVisible,
		moveUp,
		moveDown,
		moveToTop,
		moveToBottom,
		canToggleVisibility,
		toggleVisibility,
		canMoveUp,
		canMoveDown,
		onOpenChange,
	};

	if (currentLevel > 0 && fg('navx-1416-nav-item-actions-menu-refactor')) {
		return (
			<MenuList>
				<NestedMenu {...nestedMenuProps} level={currentLevel} />
			</MenuList>
		);
	}

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }: CustomTriggerProps) => (
				<IconButton
					{...props}
					ref={triggerRef}
					icon={ShowMoreHorizontalIcon}
					label={formatMessage(messages.actionsMenuTriggerLabel)}
					onClick={(event) => handleOpenChange({ isOpen: !isOpen, event })}
				/>
			)}
			shouldRenderToParent
			spacing="compact"
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
		>
			<DropdownItemGroup>
				{fg('navx-1416-nav-item-actions-menu-refactor') ? (
					<NestedMenu {...nestedMenuProps} level={currentLevel} />
				) : (
					<NestedMenu {...nestedMenuProps} />
				)}
			</DropdownItemGroup>
		</DropdownMenu>
	);
}

function NestedMenu(props: NavItemActionsProps & { level?: number }) {
	const {
		level = 0,
		isVisible,
		canToggleVisibility,
		toggleVisibility,
		isReorderable,
		...reorderingProps
	} = props;

	// Level is ignored if this refactor is not enabled - the actions menu only renders from level 0
	// and consumers (i.e., GSN) render the inner components directly.
	if (!fg('navx-1416-nav-item-actions-menu-refactor')) {
		return (
			<>
				{isReorderable && (
					<NavItemReorderingMenu {...reorderingProps} TriggerComponent={DropdownItem} />
				)}
				{canToggleVisibility && (
					<NavItemVisibilityToggle
						isVisible={isVisible}
						toggleVisibility={toggleVisibility}
						TriggerComponent={DropdownItem}
					/>
				)}
			</>
		);
	}

	return (
		<>
			{isReorderable ? (
				fg('navx-2284-nav-item-actions-layout-and-semantics') ? (
					<NavItemReorderingMenu
						{...reorderingProps}
						TriggerComponent={level === 0 ? DropdownItem : ButtonMenuItem}
					/>
				) : (
					<NavItemReorderingMenu
						{...reorderingProps}
						TriggerComponent={level === 0 ? DropdownItem : FlyoutMenuItemTrigger}
					/>
				)
			) : null}
			{canToggleVisibility && (
				<NavItemVisibilityToggle
					isVisible={isVisible}
					toggleVisibility={toggleVisibility}
					TriggerComponent={level === 0 ? DropdownItem : ButtonMenuItem}
				/>
			)}
		</>
	);
}
